import React, { useEffect, useRef, useState } from 'react';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import tw, { css } from 'twin.macro';
import { ResizablePanel } from 'components/Resizable';
import { ImperativePanelHandle } from 'react-resizable-panels';
import Button from 'components/Buttons/Button';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { MobileToggleButton } from 'components/SubSidebar/MobileToggleButton';

interface Props {
  children: React.ReactNode;
  order: number;
  collapsedComponent?: any;
  collapsedSize?: number;
  minSize?: number;
  maxSize?: number;
  defaultSize?: number;
  dynamicStyle?: (props: {
    isCollapsed?: boolean;
    isExpanded?: boolean;
  }) => any;
  className?: string;
  withCloseButton?: boolean;
  position?: 'left' | 'right';
}

export function SubSidebar(props: Props) {
  const {
    children,
    collapsedComponent,
    className,
    position = 'left',
    withCloseButton,
  } = props;
  const isMobile = useIsBelowBreakpoint('md');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const minSize = isMobile ? 80 : props.minSize;
  const collapsedSize = isMobile ? 12 : props.collapsedSize || 3;

  const ref = useRef<ImperativePanelHandle>(null); // Reference to the root DOM node

  const togglePanel = () => {
    const panel = ref.current;
    if (panel) {
      if (panel.isExpanded()) {
        panel.collapse();
      } else {
        panel.expand();
      }
    }
  };

  useEffect(() => {
    if (ref?.current) {
      setIsOpen(ref?.current?.isExpanded());
    }
  }, [ref?.current?.isExpanded()]);
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
      if (ref?.current) {
        ref?.current?.collapse();
      }
    }
  }, [isMobile]);

  return (
    <ResizablePanel
      ref={ref}
      order={props.order}
      onCollapse={() => {
        setIsOpen(false);
      }}
      onExpand={() => {
        setIsOpen(true);
      }}
      minSize={minSize}
      defaultSize={props.defaultSize || 20}
      maxSize={props.maxSize}
      collapsible={true}
      collapsedSize={collapsedSize}
      className={className}
      css={[
        // Overflow visible is for the mobile toggle button
        tw`relative overflow-visible! md:overflow-hidden! bg-white min-h-0 flex flex-col border-r `,
        isOpen ? tw`max-w-[80vw]` : tw`max-w-12 min-w-12`,
        props?.dynamicStyle?.({
          isCollapsed: ref?.current?.isCollapsed(),
          isExpanded: ref?.current?.isExpanded(),
        }),
      ]}
    >
      <div tw="h-full min-h-0 flex flex-col bottom-0 w-full md:max-w-full top-auto md:bottom-auto -mr-px translate-x-0 transform transition-transform duration-200 ease-in-out relative bg-white">
        <MobileToggleButton
          position={position}
          isOpen={isOpen}
          onClick={togglePanel}
        />
        <div
          // style={{ height: `calc(100vh - ${height}px)`, top: `${height}px` }}
          css={[
            tw`bg-white overflow-x-hidden overflow-y-auto min-h-0 flex flex-col flex-grow`,
            // On mobile we need space for the toggle button
            !isOpen && tw`pt-6 md:pt-0`,
          ]}
        >
          <div
            css={[
              isOpen && tw`right-4`,
              !isOpen &&
                css`
                  left: calc(100% - 40px);
                `,
              tw`hidden absolute top-3 z-30 mx-auto`,
              (!isOpen || withCloseButton) && tw`md:block `,
            ]}
          >
            <Button
              css={[
                tw`rounded-full px-1 py-0 bg-neutral-quiet border-transparent  transform transition-all ease-in-out `,
                isOpen && tw`hover:pr-3`,
                !isOpen && tw`hover:pl-3`,
              ]}
              small
              onClick={() => togglePanel()}
            >
              {isOpen || position === 'right' ? (
                <ChevronLeftIcon tw="h-5 w-5" />
              ) : (
                <ChevronRightIcon tw="h-5 w-5" />
              )}
            </Button>
          </div>
          {!isOpen ? (
            <div tw="md:mt-14">{collapsedComponent}</div>
          ) : (
            <>{children}</>
          )}
        </div>
      </div>
    </ResizablePanel>
  );
}
